import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FILTERS_FOR_NAMES,
  HTTP_STATUS_CODE,
  QUERY_STATUS,
} from "constants/constants";
import { ROLE_IA } from "access-roles/roles";
import { useUserContext } from "api/user/UserProvider";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { withUserScopeCheck } from "utils/helper";
import { extractApiFieldErrorsFlat } from "utils/apiError";
import { formatStaff } from "utils/modalsHelper";
import {
  createDeliveryNoteTemplateQuery,
  deleteDeliveryNoteTemplateQuery,
  dismissReceivedDeliveryNoteTemplateQuery,
  getDeliveryNoteTemplatesQuery,
  getPublicDeliveryNoteTemplatesQuery,
  getReceivedDeliveryNoteTemplatesQuery,
  importPublicDeliveryNoteTemplateQuery,
  importReceivedDeliveryNoteTemplateQuery,
  setDeliveryNoteTemplateFavoriteQuery,
  setDeliveryNoteTemplatePublicQuery,
  shareDeliveryNoteTemplateQuery,
  updateDeliveryNoteTemplateQuery,
} from "./queries";

const DeliveryNoteTemplateContext = createContext({});

export const useDeliveryNoteTemplateContext = () =>
  useContext(DeliveryNoteTemplateContext);

const patchTemplate = (templates, id, patch) =>
  templates.map((item) => (item.id === id ? { ...item, ...patch } : item));

const withSharedWithNames = (templates, nameById) =>
  templates.map((item) => ({
    ...item,
    shared_with_names: (item.shared_with || []).map(
      (id) => nameById.get(id) || ""
    ),
  }));

const withUndoneTemplateFlag = (templates, id, field, written) =>
  templates.map((item) =>
    item.id === id && item[field] === written
      ? { ...item, [field]: !written }
      : item
  );

export const DeliveryNoteTemplateProvider = ({ ...props }) => {
  const { userScope, connectedUser, setApiErrorResponse } = useUserContext();
  const { getNamesByFilters } = useGeneralContext();

  const [deliveryNoteTemplates, setDeliveryNoteTemplates] = useState([]);
  const [loadTemplatesStatus, setLoadTemplatesStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const pendingFavoritesRef = useRef(new Set());
  const pendingDeletesRef = useRef(new Map());
  const [isDeletePending, setIsDeletePending] = useState(false);
  const [receivedTemplates, setReceivedTemplates] = useState([]);
  const [loadReceivedStatus, setLoadReceivedStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const pendingReceivedRef = useRef(new Map());
  const [isReceivedActionPending, setIsReceivedActionPending] = useState(false);
  const pendingPublicToggleRef = useRef(new Set());
  const [publicTemplates, setPublicTemplates] = useState([]);
  const [loadPublicStatus, setLoadPublicStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const pendingPublicImportRef = useRef(new Map());
  const [isPublicActionPending, setIsPublicActionPending] = useState(false);
  const personNamesRef = useRef(new Map());

  const resolvePersonNames = useCallback(
    async (ids) => {
      const nameById = personNamesRef.current;
      const missing = [...new Set(ids)].filter((id) => id && !nameById.has(id));
      if (missing.length) {
        try {
          const names = await getNamesByFilters({
            types: [FILTERS_FOR_NAMES.PERSON],
            id: missing,
            include_deleted: true,
          });
          missing.forEach((id) => nameById.set(id, ""));
          (names || []).forEach((entry) =>
            nameById.set(entry.id, entry.full_name)
          );
        } catch (e) {
          console.error("resolvePersonNames", e);
        }
      }
      return nameById;
    },
    [getNamesByFilters]
  );

  const getDeliveryNoteTemplates = useCallback(async () => {
    if (!userScope) return null;
    setLoadTemplatesStatus(QUERY_STATUS.LOADING);
    try {
      const templates = await getDeliveryNoteTemplatesQuery({ userScope });
      if (!Array.isArray(templates)) {
        throw new TypeError("delivery note templates: expected a list");
      }
      setDeliveryNoteTemplates(templates);
      setLoadTemplatesStatus(QUERY_STATUS.SUCCESS);
      resolvePersonNames(
        templates.flatMap((item) => item.shared_with || [])
      ).then((nameById) =>
        setDeliveryNoteTemplates((current) =>
          withSharedWithNames(current, nameById)
        )
      );
      return templates;
    } catch (e) {
      console.error("getDeliveryNoteTemplates", e);
      setDeliveryNoteTemplates([]);
      setLoadTemplatesStatus(QUERY_STATUS.ERROR);
      return null;
    }
  }, [userScope, resolvePersonNames]);

  const resolveSenderNames = useCallback(
    async (received) => {
      const nameById = await resolvePersonNames(
        received.map((item) => item.shared_by_manager_id)
      );
      return received.map((item) => ({
        ...item,
        shared_by_name: nameById.get(item.shared_by_manager_id) || "",
      }));
    },
    [resolvePersonNames]
  );

  const getReceivedDeliveryNoteTemplates = useCallback(async () => {
    if (!userScope) return null;
    setLoadReceivedStatus(QUERY_STATUS.LOADING);
    try {
      const received = await getReceivedDeliveryNoteTemplatesQuery({
        userScope,
      });
      if (!Array.isArray(received)) {
        throw new TypeError(
          "received delivery note templates: expected a list"
        );
      }
      const resolved = await resolveSenderNames(received);
      setReceivedTemplates(resolved);
      setLoadReceivedStatus(QUERY_STATUS.SUCCESS);
      return resolved;
    } catch (e) {
      console.error("getReceivedDeliveryNoteTemplates", e);
      setReceivedTemplates([]);
      setLoadReceivedStatus(QUERY_STATUS.ERROR);
      return null;
    }
  }, [userScope, resolveSenderNames]);

  const resolvePublisherNames = useCallback(
    async (published) => {
      const nameById = await resolvePersonNames(
        published.map((item) => item.published_by_manager_id)
      );
      return published.map((item) => ({
        ...item,
        published_by_name: nameById.get(item.published_by_manager_id) || "",
      }));
    },
    [resolvePersonNames]
  );

  const getPublicDeliveryNoteTemplates = useCallback(async () => {
    if (!userScope) return null;
    setLoadPublicStatus(QUERY_STATUS.LOADING);
    try {
      const published = await getPublicDeliveryNoteTemplatesQuery({ userScope });
      if (!Array.isArray(published)) {
        throw new TypeError("public delivery note templates: expected a list");
      }
      const resolved = await resolvePublisherNames(published);
      setPublicTemplates(resolved);
      setLoadPublicStatus(QUERY_STATUS.SUCCESS);
      return resolved;
    } catch (e) {
      console.error("getPublicDeliveryNoteTemplates", e);
      setPublicTemplates([]);
      setLoadPublicStatus(QUERY_STATUS.ERROR);
      return null;
    }
  }, [userScope, resolvePublisherNames]);

  const persistDeliveryNoteTemplate = useCallback(
    async ({ label, query }) => {
      if (!userScope) {
        console.error(`${label}: userScope is not defined`);
        return { fieldErrors: {} };
      }
      try {
        await withUserScopeCheck({
          callback: () => query(userScope),
          scope: userScope,
        });
        return null;
      } catch (e) {
        console.error(label, e);
        return {
          status: e?.response?.status,
          fieldErrors: extractApiFieldErrorsFlat(
            e?.response?.data?.["bad-request"]
          ),
        };
      }
    },
    [userScope]
  );

  const reportFieldErrors = useCallback(
    (failure) => {
      if (Object.keys(failure?.fieldErrors || {}).length) {
        setApiErrorResponse(failure.fieldErrors);
      }
      return failure;
    },
    [setApiErrorResponse]
  );

  const createDeliveryNoteTemplate = useCallback(
    (data) =>
      persistDeliveryNoteTemplate({
        label: "createDeliveryNoteTemplate",
        query: (scope) =>
          createDeliveryNoteTemplateQuery({ data, userScope: scope }),
      }).then(reportFieldErrors),
    [persistDeliveryNoteTemplate, reportFieldErrors]
  );

  const updateDeliveryNoteTemplate = useCallback(
    ({ id, data }) => {
      if (!id) {
        console.error("updateDeliveryNoteTemplate: id is not defined");
        return Promise.resolve({ fieldErrors: {} });
      }
      return persistDeliveryNoteTemplate({
        label: "updateDeliveryNoteTemplate",
        query: (scope) =>
          updateDeliveryNoteTemplateQuery({ id, data, userScope: scope }),
      }).then(reportFieldErrors);
    },
    [persistDeliveryNoteTemplate, reportFieldErrors]
  );

  const toggleTemplateFlag = useCallback(
    async ({ label, template, field, pendingRef, query }) => {
      const id = template?.id;
      if (!id) {
        console.error(`${label}: id is not defined`);
        return undefined;
      }
      if (pendingRef.current.has(id)) return undefined;
      pendingRef.current.add(id);

      const written = !template[field];

      try {
        setDeliveryNoteTemplates((current) =>
          patchTemplate(current, id, { [field]: written })
        );
        const failure = await persistDeliveryNoteTemplate({
          label,
          query: (scope) => query({ id, written, scope }),
        });
        if (failure) {
          setDeliveryNoteTemplates((current) =>
            withUndoneTemplateFlag(current, id, field, written)
          );
        }
        return !failure;
      } finally {
        pendingRef.current.delete(id);
      }
    },
    [persistDeliveryNoteTemplate]
  );

  const toggleDeliveryNoteTemplateFavorite = useCallback(
    (template) =>
      toggleTemplateFlag({
        label: "toggleDeliveryNoteTemplateFavorite",
        template,
        field: "is_favorite",
        pendingRef: pendingFavoritesRef,
        query: ({ id, written, scope }) =>
          setDeliveryNoteTemplateFavoriteQuery({
            id,
            isFavorite: written,
            userScope: scope,
          }),
      }),
    [toggleTemplateFlag]
  );

  const toggleDeliveryNoteTemplatePublic = useCallback(
    (template) =>
      toggleTemplateFlag({
        label: "toggleDeliveryNoteTemplatePublic",
        template,
        field: "is_public",
        pendingRef: pendingPublicToggleRef,
        query: ({ id, written, scope }) =>
          setDeliveryNoteTemplatePublicQuery({
            id,
            isPublic: written,
            userScope: scope,
          }),
      }),
    [toggleTemplateFlag]
  );

  const searchShareColleagues = useCallback(
    async (search) => {
      try {
        const result = await getNamesByFilters({
          types: [FILTERS_FOR_NAMES.PERSON],
          role: ROLE_IA,
          search,
        });
        return formatStaff(
          (result || []).filter((person) => person?.id !== connectedUser?.id)
        );
      } catch (e) {
        console.error("searchShareColleagues", e);
        return [];
      }
    },
    [getNamesByFilters, connectedUser?.id]
  );

  const shareDeliveryNoteTemplate = useCallback(
    async ({ id, colleagues, message }) => {
      if (!id || !userScope) {
        console.error(
          `shareDeliveryNoteTemplate: ${id ? "userScope" : "id"} is not defined`
        );
        return { fieldErrors: {} };
      }
      try {
        const sharedWith = await withUserScopeCheck({
          callback: () =>
            shareDeliveryNoteTemplateQuery({
              id,
              colleagueIds: colleagues.map((colleague) => colleague.value),
              message,
            }),
          scope: userScope,
        });
        if (!Array.isArray(sharedWith)) {
          throw new TypeError("delivery note template share: expected a list");
        }
        colleagues.forEach((colleague) =>
          personNamesRef.current.set(colleague.value, colleague.label)
        );
        const nameById = await resolvePersonNames(sharedWith);
        setDeliveryNoteTemplates((current) =>
          withSharedWithNames(
            patchTemplate(current, id, { shared_with: sharedWith }),
            nameById
          )
        );
        return { sharedWith };
      } catch (e) {
        console.error("shareDeliveryNoteTemplate", e);
        return {
          fieldErrors: extractApiFieldErrorsFlat(
            e?.response?.data?.["bad-request"]
          ),
        };
      }
    },
    [userScope, resolvePersonNames]
  );

  const runRowAction = useCallback(
    ({
      label,
      key,
      keyName = "id",
      query,
      refresh,
      refreshOnStale = refresh,
      staleStatuses = [HTTP_STATUS_CODE.NOT_FOUND],
      pendingRef,
      setPending,
    }) => {
      if (!key || !userScope) {
        console.error(
          `${label}: ${key ? "userScope" : keyName} is not defined`
        );
        return Promise.resolve(null);
      }

      const inFlight = pendingRef.current.get(key);
      if (inFlight) return inFlight;

      const markPending = () => setPending(pendingRef.current.size > 0);

      const run = (async () => {
        try {
          const result = await withUserScopeCheck({
            callback: () => query(userScope),
            scope: userScope,
          });
          await refresh();
          return result || {};
        } catch (e) {
          console.error(label, e);
          if (staleStatuses.includes(e?.response?.status)) {
            await refreshOnStale();
          }
          return null;
        } finally {
          pendingRef.current.delete(key);
          markPending();
        }
      })();

      pendingRef.current.set(key, run);
      markPending();
      return run;
    },
    [userScope]
  );

  const deleteDeliveryNoteTemplate = useCallback(
    async ({ id }) =>
      (await runRowAction({
        label: "deleteDeliveryNoteTemplate",
        key: id,
        query: (scope) =>
          deleteDeliveryNoteTemplateQuery({ id, userScope: scope }),
        refresh: getDeliveryNoteTemplates,
        pendingRef: pendingDeletesRef,
        setPending: setIsDeletePending,
      })) !== null,
    [runRowAction, getDeliveryNoteTemplates]
  );

  const importReceivedDeliveryNoteTemplate = useCallback(
    (shareId) =>
      runRowAction({
        label: "importReceivedDeliveryNoteTemplate",
        key: shareId,
        keyName: "shareId",
        query: (scope) =>
          importReceivedDeliveryNoteTemplateQuery({
            shareId,
            userScope: scope,
          }),
        refresh: () =>
          Promise.all([
            getDeliveryNoteTemplates(),
            getReceivedDeliveryNoteTemplates(),
          ]),
        refreshOnStale: getReceivedDeliveryNoteTemplates,
        pendingRef: pendingReceivedRef,
        setPending: setIsReceivedActionPending,
      }),
    [
      runRowAction,
      getDeliveryNoteTemplates,
      getReceivedDeliveryNoteTemplates,
    ]
  );

  const importPublicDeliveryNoteTemplate = useCallback(
    (templateId) =>
      runRowAction({
        label: "importPublicDeliveryNoteTemplate",
        key: templateId,
        keyName: "templateId",
        query: (scope) =>
          importPublicDeliveryNoteTemplateQuery({
            templateId,
            userScope: scope,
          }),
        refresh: () =>
          Promise.all([
            getDeliveryNoteTemplates(),
            getPublicDeliveryNoteTemplates(),
          ]),
        refreshOnStale: getPublicDeliveryNoteTemplates,
        staleStatuses: [HTTP_STATUS_CODE.NOT_FOUND, HTTP_STATUS_CODE.CONFLICT],
        pendingRef: pendingPublicImportRef,
        setPending: setIsPublicActionPending,
      }),
    [runRowAction, getDeliveryNoteTemplates, getPublicDeliveryNoteTemplates]
  );

  const dismissReceivedDeliveryNoteTemplate = useCallback(
    async (shareId) =>
      (await runRowAction({
        label: "dismissReceivedDeliveryNoteTemplate",
        key: shareId,
        keyName: "shareId",
        query: (scope) =>
          dismissReceivedDeliveryNoteTemplateQuery({
            shareId,
            userScope: scope,
          }),
        refresh: getReceivedDeliveryNoteTemplates,
        pendingRef: pendingReceivedRef,
        setPending: setIsReceivedActionPending,
      })) !== null,
    [runRowAction, getReceivedDeliveryNoteTemplates]
  );

  const values = useMemo(
    () => ({
      deliveryNoteTemplates,
      loadTemplatesStatus,
      getDeliveryNoteTemplates,
      createDeliveryNoteTemplate,
      updateDeliveryNoteTemplate,
      deleteDeliveryNoteTemplate,
      isDeletePending,
      toggleDeliveryNoteTemplateFavorite,
      toggleDeliveryNoteTemplatePublic,
      searchShareColleagues,
      shareDeliveryNoteTemplate,
      receivedTemplates,
      loadReceivedStatus,
      isReceivedActionPending,
      getReceivedDeliveryNoteTemplates,
      importReceivedDeliveryNoteTemplate,
      dismissReceivedDeliveryNoteTemplate,
      publicTemplates,
      loadPublicStatus,
      isPublicActionPending,
      getPublicDeliveryNoteTemplates,
      importPublicDeliveryNoteTemplate,
    }),
    [
      deliveryNoteTemplates,
      loadTemplatesStatus,
      getDeliveryNoteTemplates,
      createDeliveryNoteTemplate,
      updateDeliveryNoteTemplate,
      deleteDeliveryNoteTemplate,
      isDeletePending,
      toggleDeliveryNoteTemplateFavorite,
      toggleDeliveryNoteTemplatePublic,
      searchShareColleagues,
      shareDeliveryNoteTemplate,
      receivedTemplates,
      loadReceivedStatus,
      isReceivedActionPending,
      getReceivedDeliveryNoteTemplates,
      importReceivedDeliveryNoteTemplate,
      dismissReceivedDeliveryNoteTemplate,
      publicTemplates,
      loadPublicStatus,
      isPublicActionPending,
      getPublicDeliveryNoteTemplates,
      importPublicDeliveryNoteTemplate,
    ]
  );

  return <DeliveryNoteTemplateContext.Provider value={values} {...props} />;
};
